import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { LOGO_URL } from "@/lib/constants";
import { formatDetail } from "@/lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Single-session enforcement: kalau user di-kick karena login di device lain,
  // tampilkan toast peringatan. Flag di-set oleh api.js interceptor saat backend
  // response 401 dgn detail === "SESSION_INVALIDATED".
  useEffect(() => {
    if (sessionStorage.getItem("ens_session_kicked") === "1") {
      sessionStorage.removeItem("ens_session_kicked");
      toast.warning("Sesi Anda dibuka di device lain — silakan login ulang.", {
        duration: 10000,
        description: "Untuk keamanan, hanya satu sesi aktif per akun. Sesi lama otomatis ditutup.",
      });
    }
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0B1D3A] text-white relative overflow-hidden">
        <div className="relative z-10">
          <img src={LOGO_URL} alt="PT Eka Nusa Sinergi" className="h-16 bg-white p-3 inline-block" />
        </div>
        <div className="relative z-10 space-y-3">
          <p className="overline text-white/60">ENERGI HUB</p>
          <h1 className="font-display text-5xl leading-tight tracking-tight">
            Cross-Division Coordination.
            <br /><span className="text-[#FF5A1F]">Faster.</span>
          </h1>
          <p className="text-white/70 max-w-md text-sm leading-relaxed">
            Orchestrate assignments across BOD, FAT, Procurement Asset Logistic, and Project divisions. Track response times, completion, and personnel performance with precision.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-6 text-xs text-white/50">
          <span>PT. EKA NUSA SINERGI</span>
          <span className="h-1 w-1 bg-white/40" />
          <span>ENERGI</span>
        </div>
        <div aria-hidden className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <img src={LOGO_URL} alt="ENS" className="h-12" />
          </div>
          <p className="overline text-slate-500 mb-3">Sign In</p>
          <h2 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Welcome Back</h2>
          <p className="text-sm text-slate-500 mt-2">Use your company email to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <Input id="email" data-testid="login-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@ensinergi.com" className="rounded-none h-11 border-slate-300" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
                <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-xs font-semibold uppercase tracking-wider text-[#FF5A1F] hover:text-[#e04a13]">Lupa Password?</Link>
              </div>
              <Input id="password" data-testid="login-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none h-11 border-slate-300" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={busy} className="w-full rounded-none h-11 bg-[#FF5A1F] hover:bg-[#e04a13] text-white font-semibold uppercase tracking-wider text-sm">
              {busy ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <div className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500">
            Need help? Contact your system administrator.
          </div>
        </div>
      </div>
    </div>
  );
}
